"use client";

import { useEffect, useState, type FC } from "react";
import {
  Airplane,
  BuildingApartment,
  CarProfileIcon,
  Circuitry,
  DroneIcon,
  Factory,
  FlowArrow,
  Gauge,
  GearSix,
  HardHat,
  Lightning,
  Radioactive,
  Ruler,
  Scales,
  Thermometer,
  TreeStructure,
  Truck,
  Wall,
  Wrench,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";

type IconRenderer = FC<{ className: string }>;

const phosphor =
  (Icon: PhosphorIcon): IconRenderer =>
  ({ className }) => (
    <div className={className}>
      <Icon weight="regular" aria-hidden="true" className="size-full" />
    </div>
  );

const material =
  (name: string): IconRenderer =>
  ({ className }) => (
    <div className={`${className} flex items-center justify-center`}>
      <span
        aria-hidden="true"
        className="material-symbols-sharp leading-none text-5xl md:text-6xl"
      >
        {name}
      </span>
    </div>
  );

const image =
  (src: string, scale = 1): IconRenderer =>
  ({ className }) => (
    <div className={className}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        style={{ transform: `scale(${scale})` }}
        className="size-full object-contain"
      />
    </div>
  );

const WindTurbine: IconRenderer = ({ className }) => (
  <div className={className}>
    <svg
      viewBox="8 4 48 56"
      fill="none"
      stroke="currentColor"
      strokeWidth={4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="size-full"
    >
      <path d="M32 22V54" />
      <path d="M28 58H36" />
      <circle cx="32" cy="22" r="3" fill="currentColor" stroke="none" />
      <path d="M32 22 L50 14" />
      <path d="M32 22 L20 6" />
      <path d="M32 22 L14 34" />
    </svg>
  </div>
);

const Satellite: IconRenderer = ({ className }) => (
  <div className={className}>
    <svg
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth={4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="size-full"
    >
      <rect x="26" y="28" width="12" height="12" />
      <rect x="8" y="30" width="14" height="8" />
      <line x1="15" y1="30" x2="15" y2="38" />
      <line x1="22" y1="34" x2="26" y2="34" />
      <rect x="42" y="30" width="14" height="8" />
      <line x1="49" y1="30" x2="49" y2="38" />
      <line x1="38" y1="34" x2="42" y2="34" />
      <line x1="32" y1="28" x2="32" y2="20" />
      <line x1="28" y1="20" x2="36" y2="20" />
      <path d="M14 50 Q18 46 22 50" />
      <path d="M10 54 Q18 42 26 54" />
    </svg>
  </div>
);

const INDUSTRIES: Array<{ name: string; icons: IconRenderer[] }> = [
  {
    name: "Construction",
    icons: [
      phosphor(Wall),
      phosphor(Circuitry),
      image("/icons/electrical.png"),
      phosphor(BuildingApartment),
    ],
  },
  {
    name: "Power systems",
    icons: [
      phosphor(Factory),
      WindTurbine,
      image("/icons/hydropower.png"),
      phosphor(Lightning),
      phosphor(Radioactive),
    ],
  },
  {
    name: "Vehicles",
    icons: [
      phosphor(CarProfileIcon),
      phosphor(Truck),
      image("/icons/cargo-ship.png", 1.75),
      phosphor(Airplane),
      phosphor(DroneIcon),
      Satellite,
    ],
  },
  {
    name: "Manufacturing",
    icons: [
      phosphor(Wrench),
      image("/icons/robotic-arm.png"),
      image("/icons/tools.png"),
  
    ],
  },
  {
    name: "Processes",
    icons: [
      phosphor(TreeStructure),
      phosphor(FlowArrow),
      phosphor(GearSix),
    ],
  },
  {
    name: "Properties",
    icons: [
      phosphor(Ruler),
      phosphor(Scales),
      phosphor(Thermometer),
      phosphor(Gauge),
    ],
  },
];

const INTERVAL_MS = 3000;

function RotatingIcon({ icons }: { icons: IconRenderer[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (icons.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % icons.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [icons.length]);

  return (
    <div className="relative size-10 md:size-12">
      {icons.map((Icon, i) => {
        const active = i === index;
        return (
          <Icon
            key={i}
            className={`absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              active
                ? "opacity-100 scale-100 blur-0"
                : "opacity-0 scale-90 blur-[2px]"
            }`}
          />
        );
      })}
    </div>
  );
}

export function IndustryGrid() {
  return (
    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {INDUSTRIES.map(({ name, icons }) => (
        <li
          key={name}
          className="p-4 flex flex-col items-center text-center gap-2"
        >
          <RotatingIcon icons={icons} />
          <span className="text-sm md:text-base leading-tight">{name}</span>
        </li>
      ))}
    </ul>
  );
}
